import { MutableRefObject, useEffect, useState } from 'react'

import { useOpenHandler } from 'ui/components/Select/hooks'
import { ChoiceOption, ChoiceValue } from 'ui/types'

import { ModelData } from '../types'

const getRefilledModelData = (newData: ChoiceOption[], oldModelData: ModelData[], selectedValues: ChoiceValue[]) => {
  const selectedOptionsNotInNewData = oldModelData.filter(
    (oldDataEl) =>
      oldDataEl.selected && newData.find((newDataEl) => newDataEl.value === oldDataEl.data.value) === undefined
  )

  const newModelData = selectedOptionsNotInNewData.concat(
    (newData || []).map((el, index) => ({
      data: el,
      index,
      selected: (selectedValues || []).includes(el.value)
    }))
  )

  newModelData.forEach((el, index) => (el.index = index))

  return newModelData
}

export const useManageState = (
  inputData: ChoiceOption[],
  selectedValues: ChoiceValue[],
  inputRef: MutableRefObject<HTMLInputElement | null>,
  selectionCallback: (values: ChoiceValue[]) => void
) => {
  const [initialData, setInitialData] = useState<ChoiceOption[]>(inputData || [])
  const [searchText, setSearchText] = useState('')
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const { open, handleOpen, handleClose } = useOpenHandler(inputRef)

  // support initial render with passed data options, so fill model data by input data independently
  // from isPreferServerData
  const [modelData, setModelData] = useState<ModelData[]>(getRefilledModelData(inputData, [], selectedValues))

  // sync data if it will change outside,
  useEffect(() => {
    setModelData(
      inputData
        ? inputData.map((data, index) => ({
            data,
            index,
            selected: (selectedValues || []).includes(data.value)
          }))
        : []
    )
    setInitialData(inputData || [])
    // run effect only on input data changing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(inputData)])

  useEffect(() => {
    const updatedModelData = modelData.map((el) => ({
      data: el.data,
      index: el.index,
      selected: (selectedValues || []).includes(el.data.value)
    }))

    setModelData([...updatedModelData])
  }, [JSON.stringify(selectedValues)])

  const setSearchedDataWrapper = (searchedData: ChoiceOption[]) => {
    setActiveIndex(null)
    setModelData([...getRefilledModelData(searchedData, modelData, selectedValues)])
  }

  const selectValue = (value: ChoiceValue) => {
    if (isSearching) {
      return
    }
    const newSelectedValues = [value].concat(selectedValues)
    selectionCallback(newSelectedValues)
  }

  const selectAllValues = (values: ChoiceValue[]) => {
    if (isSearching) {
      return
    }
    const newSelectedValues = [...values].concat(selectedValues)
    selectionCallback(newSelectedValues)
  }

  const unselectValue = (value: ChoiceValue) => {
    if (isSearching) {
      return
    }

    const indexToRemove = selectedValues.indexOf(value)
    if (indexToRemove === -1) {
      // make exception?
      return
    }
    const newSelectedValues = [...selectedValues]
    newSelectedValues.splice(indexToRemove, 1)
    selectionCallback(newSelectedValues)
  }

  const unselectAllValues = () => {
    if (isSearching) {
      return
    }
    selectionCallback([])
  }

  const handleCloseCallback = () => {
    handleClose()
    setActiveIndex(null)
    setSearchText('')
    setIsSearching(false)
    // waiting for close animation to be done, to exclude options blinking
    if (initialData.length) {
      setTimeout(() => setSearchedDataWrapper(initialData), 250)
    }
  }

  return {
    initialData,
    searchText,
    selectedValues,
    activeIndex,
    isSearching,
    modelData,
    open,
    inputValue: searchText,
    setSearchedData: setSearchedDataWrapper,
    setIsSearching,
    setActiveIndex,
    setSearchText,
    unselectValue,
    selectValue,
    selectAllValues,
    unselectAllValues,
    handleClose: handleCloseCallback,
    handleOpen
  }
}
